import { useState, useEffect, useRef } from "react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from "recharts";

const API_BASE = "http://localhost:8000";

// ─── Constants (mirrored from RemediationPunchList) ──────────────────────────

const CATEGORY_LABELS = {
  identity_access:          "Identity & Access Mgmt",
  network_security:         "Network Security",
  endpoint_security:        "Endpoint Security",
  endpoint_protection:      "Endpoint Protection",
  data_protection:          "Data Protection",
  incident_response:        "Incident Response",
  vulnerability_management: "Vulnerability Mgmt",
  cloud_security:           "Cloud Security",
  application_security:     "Application Security",
  third_party_risk:         "Third-Party Risk",
  compliance_governance:    "Compliance & Governance",
  security_awareness:       "Security Awareness",
  physical_security:        "Physical Security",
  business_continuity:      "Business Continuity",
  backup_recovery:          "Backup & Recovery",
  logging_monitoring:       "Logging & Monitoring",
  asset_management:         "Asset Management",
};

const TASK_TEMPLATES = {
  identity_access: ["Implement MFA for privileged accounts","Conduct user access review","Deploy PAM solution","Enforce least-privilege policies"],
  network_security: ["Harden firewall ruleset","Implement network segmentation","Deploy IDS/IPS","Document change management process"],
  data_protection: ["Inventory and classify sensitive data","Encrypt customer financial data at rest","Deploy DLP tooling","Establish data retention procedures"],
  incident_response: ["Develop Incident Response Plan","Conduct tabletop exercise","Establish 24/7 escalation path","Integrate IR with regulatory notifications"],
  vulnerability_management: ["Establish vulnerability scanning schedule","Define patching SLAs by severity","Integrate findings into risk register","Conduct annual penetration test"],
  cloud_security: ["Enable CSPM tooling","Review IAM policies across cloud accounts","Enable CloudTrail audit logging","Implement cloud workload protection"],
  endpoint_security: ["Deploy EDR on all endpoints","Enforce disk encryption on laptops","Implement USB controls","Establish endpoint compliance monitoring"],
  endpoint_protection: ["Deploy next-gen antivirus","Enable application whitelisting","Configure automated threat detection","Establish patch compliance monitoring"],
  application_security: ["Implement SAST/DAST in CI/CD","Conduct OWASP Top 10 review","Establish secure code review process","Deploy WAF for public-facing apps"],
  third_party_risk: ["Complete vendor inventory","Implement security questionnaire process","Review vendor contracts","Establish ongoing vendor monitoring"],
  compliance_governance: ["Develop Information Security Policy","Assign Qualified Individual","Establish Board reporting cadence","Complete regulatory gap assessment"],
  security_awareness: ["Launch mandatory security training","Conduct phishing simulation","Establish role-based training","Create security awareness metrics"],
  physical_security: ["Audit physical access controls","Implement visitor logging","Review CCTV coverage","Conduct physical security risk assessment"],
  business_continuity: ["Develop BCP documentation","Conduct annual BCP tabletop","Define and test RTOs","Establish crisis communications procedures"],
  backup_recovery: ["Verify backup completeness quarterly","Implement immutable backup","Document and test DR runbook","Establish backup monitoring"],
  logging_monitoring: ["Deploy SIEM with baseline alerting","Enable audit logging on financial systems","Establish daily log review","Configure suspicious auth alerting"],
  asset_management: ["Complete hardware/software inventory","Implement automated asset discovery","Establish lifecycle management","Define decommissioning standards"],
};

const GAP_TO_PRIORITY = (gap) => {
  if (gap >= 3) return "critical";
  if (gap >= 2) return "high";
  if (gap >= 1) return "medium";
  return "low";
};

const PHASE_FROM_PRIORITY = (p) => ({ critical: 1, high: 1, medium: 2, low: 3 }[p] || 2);

const PHASE_DAYS = { 1: [0, 90], 2: [90, 180], 3: [180, 365] };

// Cost estimates per task priority (rough placeholder — customer can edit)
const TASK_COST = { critical: 15000, high: 8000, medium: 4000, low: 2000 };

// EAL reduction contribution per priority (% of total EAL reduction)
const EAL_WEIGHT = { critical: 0.35, high: 0.25, medium: 0.25, low: 0.15 };

// ─── Task generation (same logic as RemediationPunchList) ────────────────────

function generateTasksFromGaps(responses) {
  const tasks = [];
  let id = 1;
  const sorted = Object.entries(responses)
    .map(([key, val]) => ({ key, gap: (val.target||5)-(val.score||0), current: val.score||0, target: val.target||5 }))
    .filter(g => g.gap > 0)
    .sort((a, b) => b.gap - a.gap);

  for (const { key, gap, current, target } of sorted) {
    const templates = TASK_TEMPLATES[key] || [`Improve ${CATEGORY_LABELS[key]||key} from Level ${current} to Level ${target}`];
    const priority = GAP_TO_PRIORITY(gap);
    const phase = PHASE_FROM_PRIORITY(priority);
    for (const title of templates) {
      tasks.push({
        id: `task_${id++}`, title, category: key,
        categoryLabel: CATEGORY_LABELS[key] || key,
        priority, phase, status: "not_started",
        estimatedCost: TASK_COST[priority],
        ealWeight: EAL_WEIGHT[priority] / templates.length,
      });
    }
  }
  return tasks;
}

// ─── Chart data builders ─────────────────────────────────────────────────────

function buildEALProjection(tasks, baseEAL) {
  // Simulate EAL reduction as tasks complete by phase
  const phaseWeights = { 1: 0, 2: 0, 3: 0 };
  for (const t of tasks) phaseWeights[t.phase] = (phaseWeights[t.phase] || 0) + t.ealWeight;

  const points = [];
  const months = [0, 1, 2, 3, 4, 5, 6, 9, 12];
  for (const m of months) {
    const days = m * 30;
    let reduction = 0;
    if (days >= 90)  reduction += phaseWeights[1] * 0.9;
    if (days >= 45)  reduction += phaseWeights[1] * 0.5 * (days < 90 ? (days - 45) / 45 : 1);
    if (days >= 180) reduction += phaseWeights[2] * 0.9;
    if (days >= 135) reduction += phaseWeights[2] * 0.5 * (days < 180 ? (days - 135) / 45 : 1);
    if (days >= 365) reduction += phaseWeights[3] * 0.85;
    if (days >= 270) reduction += phaseWeights[3] * 0.5 * (days < 365 ? (days - 270) / 95 : 1);

    reduction = Math.min(reduction, 0.72); // cap at 72% max reduction
    points.push({
      month: m === 0 ? "Now" : `M${m}`,
      eal: Math.round(baseEAL * (1 - reduction) / 1000) * 1000,
      target: Math.round(baseEAL * 0.28 / 1000) * 1000,
    });
  }
  return points;
}

function buildBudgetData(tasks) {
  const byPhase = { 1: 0, 2: 0, 3: 0 };
  for (const t of tasks) byPhase[t.phase] = (byPhase[t.phase]||0) + t.estimatedCost;
  const total = byPhase[1] + byPhase[2] + byPhase[3];
  return [
    { phase: "Phase 1\n0–90 days",  budget: byPhase[1], pct: Math.round(byPhase[1]/total*100) },
    { phase: "Phase 2\n90–180 days", budget: byPhase[2], pct: Math.round(byPhase[2]/total*100) },
    { phase: "Phase 3\n180+ days",  budget: byPhase[3], pct: Math.round(byPhase[3]/total*100) },
  ];
}

function buildGanttData(tasks) {
  // Group tasks by category, find phase range per category
  const cats = {};
  for (const t of tasks) {
    if (!cats[t.category]) cats[t.category] = { label: t.categoryLabel, phases: new Set(), priority: t.priority };
    cats[t.category].phases.add(t.phase);
  }
  return Object.entries(cats).map(([key, val]) => {
    const phases = [...val.phases].sort();
    const startDay = PHASE_DAYS[phases[0]][0];
    const endDay   = PHASE_DAYS[phases[phases.length-1]][1];
    return { key, label: val.label, startDay, endDay, duration: endDay - startDay, priority: val.priority };
  }).sort((a, b) => a.startDay - b.startDay || b.duration - a.duration);
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const EALTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#111827", border: "1px solid #1a2236", borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ fontSize: 13, color: p.color, fontWeight: 600 }}>
          {p.name === "eal" ? "Projected EAL" : "Target EAL"}: ${(p.value/1000000).toFixed(2)}M
        </div>
      ))}
    </div>
  );
};

const BudgetTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#111827", border: "1px solid #1a2236", borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{label?.replace("\n", " ")}</div>
      <div style={{ fontSize: 13, color: "#a78bfa", fontWeight: 700 }}>
        ${payload[0].value.toLocaleString()}
      </div>
    </div>
  );
};

// ─── Gantt Bar Component ──────────────────────────────────────────────────────

const PRIORITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#22c55e" };
const TOTAL_DAYS = 365;

function GanttRow({ item, index }) {
  const leftPct  = (item.startDay / TOTAL_DAYS) * 100;
  const widthPct = (item.duration / TOTAL_DAYS) * 100;
  const color    = PRIORITY_COLORS[item.priority] || "#64748b";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
      opacity: 0, animation: `fadeSlideIn 0.3s ease forwards`, animationDelay: `${index * 0.04}s` }}>
      <div style={{ width: 160, fontSize: 11, color: "#94a3b8", textAlign: "right",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 }}>
        {item.label}
      </div>
      <div style={{ flex: 1, height: 22, background: "#0f1420", borderRadius: 4, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: 3, height: 16, borderRadius: 3,
          left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%`,
          background: `linear-gradient(90deg, ${color}cc, ${color}66)`,
          border: `1px solid ${color}60`,
          display: "flex", alignItems: "center", paddingLeft: 6,
        }}>
          <span style={{ fontSize: 9, color: "#fff", fontWeight: 700, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.startDay}d – {item.endDay}d
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ExecutionDashboard() {
  const [tasks, setTasks]       = useState([]);
  const [baseEAL, setBaseEAL]   = useState(23500000);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [editingEAL, setEditingEAL] = useState(false);
  const [ealInput, setEalInput] = useState("23500000");

  useEffect(() => {
    fetchData();
    // Inject keyframe animation
    const style = document.createElement("style");
    style.textContent = `@keyframes fadeSlideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:none; } }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const customerId = localStorage.getItem("customer_id") || "cust_techcorpin_575148";

      // Fetch assessment for task generation
      const asmRes = await fetch(`${API_BASE}/api/assessments/current?customer_id=${customerId}`);
      if (!asmRes.ok) throw new Error("Failed to load assessment");
      const asmData = await asmRes.json();
      const responses = asmData.assessment?.responses || asmData.responses || {};
      setTasks(generateTasksFromGaps(responses));

      try {
        const token = localStorage.getItem("token");
        const ealRes = await fetch(`${API_BASE}/api/risk/latest`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (ealRes.ok) {
          const ealData = await ealRes.json();
          if (ealData.eal) {
            setBaseEAL(ealData.eal);
            setEalInput(String(ealData.eal));
          }
        }
      } catch (ealErr) {
        console.warn("Could not fetch EAL from risk API, using default", ealErr);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEALSave = () => {
    const val = parseFloat(ealInput.replace(/[^0-9.]/g, ""));
    if (!isNaN(val) && val > 0) setBaseEAL(val);
    setEditingEAL(false);
  };

  if (loading) return <div style={{ ...styles.page, display:"flex", alignItems:"center", justifyContent:"center", color:"#475569" }}>Loading execution data...</div>;
  if (error)   return <div style={{ ...styles.page, display:"flex", alignItems:"center", justifyContent:"center", color:"#ef4444" }}>⚠ {error}</div>;

  const ealData    = buildEALProjection(tasks, baseEAL);
  const budgetData = buildBudgetData(tasks);
  const ganttData  = buildGanttData(tasks);

  const totalBudget   = budgetData.reduce((s, d) => s + d.budget, 0);
  const finalEAL      = ealData[ealData.length - 1]?.eal || 0;
  const ealReduction  = baseEAL - finalEAL;
  const roi           = totalBudget > 0 ? ((ealReduction - totalBudget) / totalBudget * 100).toFixed(0) : 0;
  const phase1Tasks   = tasks.filter(t => t.phase === 1).length;
  const phase2Tasks   = tasks.filter(t => t.phase === 2).length;
  const phase3Tasks   = tasks.filter(t => t.phase === 3).length;

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Execution Dashboard</h1>
          <p style={styles.subtitle}>Risk reduction projections and remediation timeline for executive reporting</p>
        </div>
        <div style={styles.ealControl}>
          <div style={styles.ealLabel}>Baseline EAL</div>
          {editingEAL ? (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input
                style={styles.ealInput}
                value={ealInput}
                onChange={e => setEalInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleEALSave()}
                autoFocus
              />
              <button style={styles.ealSave} onClick={handleEALSave}>Save</button>
            </div>
          ) : (
            <div style={styles.ealValue} onClick={() => setEditingEAL(true)}>
              ${(baseEAL/1000000).toFixed(2)}M <span style={styles.ealEdit}>✎</span>
            </div>
          )}
        </div>
      </div>

      {/* ── KPI Strip ── */}
      <div style={styles.kpiStrip}>
        {[
          { label: "Total Remediation Tasks",  value: tasks.length,                            color: "#e2e8f0" },
          { label: "Phase 1 Tasks (0–90d)",    value: phase1Tasks,                             color: "#ef4444" },
          { label: "Phase 2 Tasks (90–180d)",  value: phase2Tasks,                             color: "#f59e0b" },
          { label: "Phase 3 Tasks (180–365d)", value: phase3Tasks,                             color: "#22c55e" },
          { label: "Estimated Investment",     value: `$${(totalBudget/1000).toFixed(0)}K`,   color: "#a78bfa" },
          { label: "EAL Reduction (12mo)",     value: `$${(ealReduction/1000000).toFixed(1)}M`, color: "#34d399" },
          { label: "Projected ROI",            value: `${roi}%`,                              color: "#38bdf8" },
        ].map((k) => (
          <div key={k.label} style={styles.kpi}>
            <div style={{ ...styles.kpiValue, color: k.color }}>{k.value}</div>
            <div style={styles.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={styles.content}>
        {/* ── Row 1: EAL Projection + Budget ── */}
        <div style={styles.row2}>
          {/* EAL Projection */}
          <div style={{ ...styles.card, flex: 3 }}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Risk Reduction Projection</div>
                <div style={styles.cardSub}>Expected Annual Loss over 12-month remediation period</div>
              </div>
              <div style={styles.projectionStat}>
                <span style={{ color: "#34d399", fontWeight: 800, fontSize: 18 }}>
                  −{Math.round((ealReduction/baseEAL)*100)}%
                </span>
                <span style={{ color: "#475569", fontSize: 11 }}>EAL Reduction</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={ealData} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                <defs>
                  <linearGradient id="ealGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="targetGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2236" />
                <XAxis dataKey="month" tick={{ fill: "#475569", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: "#475569", fontSize: 11 }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v/1000000).toFixed(1)}M`}
                />
                <Tooltip content={<EALTooltip />} />
                <ReferenceLine y={finalEAL} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.5} />
                <Area type="monotone" dataKey="eal"    stroke="#ef4444" strokeWidth={2} fill="url(#ealGrad)"    name="eal" />
                <Area type="monotone" dataKey="target" stroke="#22c55e" strokeWidth={1.5} fill="url(#targetGrad)" strokeDasharray="4 4" name="target" />
              </AreaChart>
            </ResponsiveContainer>
            <div style={styles.chartLegend}>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, background:"#ef4444" }}/>Projected EAL</span>
              <span style={styles.legendItem}><span style={{ ...styles.legendDot, background:"#22c55e" }}/>Target EAL (post-remediation)</span>
            </div>
          </div>

          {/* Budget */}
          <div style={{ ...styles.card, flex: 2 }}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Budget by Phase</div>
                <div style={styles.cardSub}>Estimated investment allocation</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={budgetData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2236" vertical={false} />
                <XAxis
                  dataKey="phase"
                  tick={{ fill: "#475569", fontSize: 10 }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => v.split("\n")[0]}
                />
                <YAxis
                  tick={{ fill: "#475569", fontSize: 10 }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}K`}
                />
                <Tooltip content={<BudgetTooltip />} />
                <Bar dataKey="budget" radius={[6,6,0,0]}
                  fill="url(#budgetGrad)"
                  label={{ position:"top", fill:"#64748b", fontSize:10, formatter: v => `$${(v/1000).toFixed(0)}K` }}
                >
                  <defs>
                    <linearGradient id="budgetGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" />
                      <stop offset="100%" stopColor="#4f46e5" />
                    </linearGradient>
                  </defs>
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={styles.budgetTotal}>
              Total: <strong style={{ color:"#a78bfa" }}>${totalBudget.toLocaleString()}</strong>
            </div>
          </div>
        </div>

        {/* ── Row 2: Gantt Chart ── */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardTitle}>Remediation Timeline</div>
              <div style={styles.cardSub}>Security capability improvements across 12-month programme</div>
            </div>
            <div style={styles.ganttLegend}>
              {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
                <span key={p} style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: c }} />
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </span>
              ))}
            </div>
          </div>

          {/* Gantt axis */}
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10, paddingLeft:172 }}>
            {[0, 90, 180, 270, 365].map(d => (
              <div key={d} style={{ position:"relative", flex: d===0 ? 0 : 1, fontSize:10, color:"#334155" }}>
                {d === 0 ? "Day 0" : `Day ${d}`}
              </div>
            ))}
          </div>

          {/* Phase bands */}
          <div style={{ position:"relative", paddingLeft:172, marginBottom:16 }}>
            <div style={{ display:"flex", height:6, borderRadius:3, overflow:"hidden" }}>
              <div style={{ flex:90, background:"#ef444420", borderRight:"1px solid #ef444440" }} />
              <div style={{ flex:90, background:"#f59e0b20", borderRight:"1px solid #f59e0b40" }} />
              <div style={{ flex:185, background:"#22c55e20" }} />
            </div>
            <div style={{ display:"flex", fontSize:9, color:"#334155", marginTop:3 }}>
              <div style={{ flex:90, textAlign:"center" }}>Phase 1</div>
              <div style={{ flex:90, textAlign:"center" }}>Phase 2</div>
              <div style={{ flex:185, textAlign:"center" }}>Phase 3</div>
            </div>
          </div>

          <div style={{ maxHeight: 420, overflowY:"auto", paddingRight:4 }}>
            {ganttData.map((item, i) => (
              <GanttRow key={item.key} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* ── Row 3: Phase Summary Table ── */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <div style={styles.cardTitle}>Phase Summary</div>
              <div style={styles.cardSub}>Investment and risk reduction by phase</div>
            </div>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Phase","Timeline","Tasks","Est. Investment","EAL Reduction","Priority Focus"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {
                  phase:"Phase 1", timeline:"0–90 days",
                  tasks: phase1Tasks,
                  budget: budgetData[0]?.budget || 0,
                  ealRed: `~${Math.round((EAL_WEIGHT.critical + EAL_WEIGHT.high) * 90 * 100)}%`,
                  focus: "Critical & High gaps",
                  color:"#ef4444",
                },
                {
                  phase:"Phase 2", timeline:"90–180 days",
                  tasks: phase2Tasks,
                  budget: budgetData[1]?.budget || 0,
                  ealRed: `~${Math.round(EAL_WEIGHT.medium * 90 * 100)}%`,
                  focus: "Medium priority gaps",
                  color:"#f59e0b",
                },
                {
                  phase:"Phase 3", timeline:"180–365 days",
                  tasks: phase3Tasks,
                  budget: budgetData[2]?.budget || 0,
                  ealRed: `~${Math.round(EAL_WEIGHT.low * 85 * 100)}%`,
                  focus: "Maturity advancement",
                  color:"#22c55e",
                },
              ].map((row, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={{ ...styles.phasePill, borderColor: row.color, color: row.color }}>
                      {row.phase}
                    </span>
                  </td>
                  <td style={styles.td}>{row.timeline}</td>
                  <td style={styles.td}>{row.tasks}</td>
                  <td style={styles.td}>${row.budget.toLocaleString()}</td>
                  <td style={{ ...styles.td, color: "#34d399", fontWeight:600 }}>{row.ealRed}</td>
                  <td style={{ ...styles.td, color:"#64748b" }}>{row.focus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight:"100vh", background:"#060910",
    color:"#e2e8f0",
    fontFamily:"'IBM Plex Sans', 'DM Sans', system-ui, sans-serif",
  },
  header: {
    display:"flex", justifyContent:"space-between", alignItems:"flex-start",
    padding:"28px 40px 20px", borderBottom:"1px solid #111827",
  },
  headerLeft: { display:"flex", flexDirection:"column", gap:5 },
  title: { fontSize:24, fontWeight:800, color:"#f1f5f9", margin:0, letterSpacing:"-0.03em" },
  subtitle: { fontSize:13, color:"#475569", margin:0 },
  ealControl: {
    display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4,
  },
  ealLabel: { fontSize:10, color:"#475569", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase" },
  ealValue: {
    fontSize:22, fontWeight:800, color:"#f97316",
    cursor:"pointer", letterSpacing:"-0.02em",
  },
  ealEdit: { fontSize:13, color:"#475569", marginLeft:4 },
  ealInput: {
    background:"#111827", border:"1px solid #f97316",
    borderRadius:6, padding:"6px 10px",
    color:"#f97316", fontSize:18, fontWeight:700,
    width:160, outline:"none",
  },
  ealSave: {
    padding:"6px 14px", background:"#f97316",
    border:"none", borderRadius:6,
    color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer",
  },
  kpiStrip: {
    display:"flex", gap:0,
    borderBottom:"1px solid #111827",
    background:"#080b12",
    overflowX:"auto",
  },
  kpi: {
    display:"flex", flexDirection:"column", gap:3,
    padding:"16px 28px",
    borderRight:"1px solid #111827",
    flexShrink:0,
  },
  kpiValue: { fontSize:22, fontWeight:800, lineHeight:1 },
  kpiLabel: { fontSize:10, color:"#334155", letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1.3 },
  content: { padding:"24px 40px", display:"flex", flexDirection:"column", gap:20 },
  row2: { display:"flex", gap:20 },
  card: {
    background:"#0c0f1a", border:"1px solid #111827",
    borderRadius:12, padding:"22px 24px",
  },
  cardHeader: {
    display:"flex", justifyContent:"space-between", alignItems:"flex-start",
    marginBottom:18,
  },
  cardTitle: { fontSize:15, fontWeight:700, color:"#f1f5f9", marginBottom:3 },
  cardSub: { fontSize:12, color:"#475569" },
  projectionStat: { display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 },
  chartLegend: { display:"flex", gap:20, marginTop:12, justifyContent:"center" },
  ganttLegend: { display:"flex", gap:16 },
  legendItem: { display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#64748b" },
  legendDot: { width:8, height:8, borderRadius:"50%", flexShrink:0 },
  budgetTotal: { textAlign:"center", fontSize:13, color:"#64748b", marginTop:8 },
  table: { width:"100%", borderCollapse:"collapse" },
  th: {
    fontSize:10, fontWeight:700, color:"#334155",
    letterSpacing:"0.08em", textTransform:"uppercase",
    padding:"8px 12px", textAlign:"left",
    borderBottom:"1px solid #111827",
  },
  tr: { borderBottom:"1px solid #0f1420" },
  td: { fontSize:13, color:"#94a3b8", padding:"12px 12px" },
  phasePill: {
    fontSize:11, fontWeight:700,
    border:"1px solid", borderRadius:5,
    padding:"2px 8px",
  },
};

